import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of, throwError } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { catchError, map, switchMap, take, tap } from "rxjs/operators";
import { environment } from "environments/environment";
import { BaseRespone, PaginationResult } from "app/core/models/common.response";
import { BuiltUpArea } from "./builtup-area.types";

@Injectable({
    providedIn: 'root'
})
export class BuiltUpAreaService {
    // Private
    private _builtUpArea: BehaviorSubject<BuiltUpArea | null> = new BehaviorSubject(null);
    private _builtUpAreas: BehaviorSubject<BuiltUpArea[] | null> = new BehaviorSubject(null);
    constructor(private _httpClient: HttpClient) {
    }

    /**
     * Getter for builtUpArea
     */
    get builtUpArea$(): Observable<BuiltUpArea> {
        return this._builtUpArea.asObservable();
    }

    /**
     * Getter for builtUpAreas
     */
    get builtUpAreas$(): Observable<BuiltUpArea[]> {
        return this._builtUpAreas.asObservable();
    }

    /**
     * Get builtUpAreas
     */
    getBuiltUpAreas(reportId: number): Observable<BaseRespone<BuiltUpArea[]>> {
        return this._httpClient.get<BaseRespone<BuiltUpArea[]>>(`${environment.baseUrl}/Report/GetBuiltUpAreaBreakUplist/${reportId}`).pipe(
            tap((response) => {
                this._builtUpAreas.next(response.result);
            })
        );
    }

    /**
      * Create builtUpArea
      */
    createBuiltUpArea(builtUpArea: BuiltUpArea): Observable<BaseRespone<BuiltUpArea>> {
        return this.builtUpAreas$.pipe(
            take(1),
            switchMap(builtUpAreas => this._httpClient.post<BaseRespone<BuiltUpArea>>(`${environment.baseUrl}/Report/AddBuiltUpAreaBreakUp`, builtUpArea)
                .pipe(
                    catchError((error) => {
                        return of(error.error);
                    }),
                    map((response) => {
                        // Return the new product
                        if (response.isSuccess) {
                            this._builtUpAreas.next([response.result, ...builtUpAreas]);

                        }
                        return response;
                    })
                ))
        );
    }

    /**
     * Get board
     *
     * @param id
     */
    getBuiltUpArea(id: number): Observable<BaseRespone<BuiltUpArea>> {
        return this._httpClient.get(`${environment.baseUrl}/Report/GetBuiltUpAreaBreakUp/${id}`).pipe(
            catchError((error) => {
                return of(error.error);
            }),
            map(response => response),
            tap(board => this._builtUpArea.next(board.result))
        );
    }


    /**
    * Get contact by id
    */
    getBuiltUpAreaById(id: number): Observable<BuiltUpArea> {
        return this._builtUpAreas.pipe(
            take(1),
            map((builtUpAreas) => {

                // Find the contact
                const builtUpArea = builtUpAreas.find(item => item.id === id) || null;

                // Update the builtUpArea
                this._builtUpArea.next(builtUpArea);

                // Return the builtUpArea
                return builtUpArea;
            }),
            switchMap((builtUpArea) => {

                if (!builtUpArea) {
                    return throwError('Could not found builtUpArea with id of ' + id + '!');
                }

                return of(builtUpArea);
            })
        );
    }

    /**
     * Update the card
     *
     * @param id
     * @param card
     */
    updateBuiltUpArea(builtUpArea: BuiltUpArea): Observable<BaseRespone<BuiltUpArea>> {
        return this.builtUpAreas$.pipe(
            take(1),
            switchMap(builtUpAreas => this._httpClient.put<BaseRespone<BuiltUpArea>>(`${environment.baseUrl}/Report/UpdateBuiltUpAreaBreakUp`, builtUpArea).pipe(
                map((builtUpAreaCard) => {
                    const index = builtUpAreas.findIndex(item => item.id === builtUpArea.id);

                    // Update the product
                    if (builtUpAreaCard.isSuccess) {
                        builtUpAreas[index] = builtUpAreaCard.result;

                        // Update the products
                        this._builtUpAreas.next(builtUpAreas);

                        this._builtUpArea.next(builtUpAreaCard.result);
                    }
                    // Return the updated card
                    return builtUpAreaCard;
                }), catchError((error) => {
                    return of(error.error);
                })
            ))
        );
    }

    deleteBuiltUpArea(id: number): Observable<BaseRespone<boolean>> {
        return this.builtUpAreas$.pipe(
            take(1),
            switchMap(builtUpAreas => this._httpClient.delete(`${environment.baseUrl}/Report/DeleteBuiltUpAreaBreakUp/${id}`).pipe(
                map((deleted: BaseRespone<boolean>) => {

                    // Find the index of the deleted product
                    const index = builtUpAreas.findIndex(item => item.id === id);

                    // Delete the product
                    builtUpAreas.splice(index, 1);

                    // Update the products
                    this._builtUpAreas.next(builtUpAreas);

                    // Return the deleted status
                    return deleted;
                })
            ))
        );
    }

    deleteAllBuiltUpArea(ids: number[]): Observable<BaseRespone<boolean>> {
        const requestBody = { ids: ids };

        return this.builtUpAreas$.pipe(
            take(1),
            switchMap(builtUpAreas => this._httpClient.delete(`${environment.baseUrl}/Report/DeleteAllBuiltUpAreaBreakUp`, {
                body: requestBody
            }).pipe(
                map((deleted: BaseRespone<boolean>) => {

                    ids.forEach(id => {
                        // Find the index of the deleted product
                        const index = builtUpAreas.findIndex(item => item.id === id);
                        // Delete the product
                        builtUpAreas.splice(index, 1);
                    });
                    // Update the products
                    this._builtUpAreas.next(builtUpAreas);

                    // Return the deleted status
                    return deleted;
                })
            ))
        );

    }

}
